import { Button } from 'designSystem/components/Button'
import { Tabs, TabsList, TabsTrigger } from 'designSystem/components/Tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import {
  TabAddButtonSlot,
  TabCloseButtonSlot,
  TabDropIndicator,
  TabIconSlot,
  TabsWrapper,
  TabTitle,
  TabTitleText,
  Toolbar,
} from 'features/knowledgeDocumentViewer/components/toolbar/documentTabsStyles'
import type { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import { Plus, X } from 'lucide-react'
import React, {
  type DragEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { useDragReorder } from 'utils/useDragReorder'

import { getWorkspaceTabIcon } from './getWorkspaceTabIcon'
import { getWorkspaceTabLabel } from './getWorkspaceTabLabel'
import { useWorkspaceTabs } from './WorkspaceTabsContext'

/**
 * Shows the full label on hover when the title is truncated. Hover target
 * covers icon + title. Re-measures when the tab becomes selected so
 * overflowing active tabs still get a tooltip without mouse leave/re-enter.
 */
function WorkspaceTabTitleTooltip({
  label,
  isActive,
  children,
}: {
  label: string
  isActive: boolean
  children: React.ReactNode
}) {
  const textRef = useRef<HTMLSpanElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const measureOverflow = useCallback(() => {
    const text = textRef.current
    if (!text) {
      setIsOverflowing(false)
      return
    }
    setIsOverflowing(text.scrollWidth > text.clientWidth + 1)
  }, [])

  useEffect(() => {
    measureOverflow()
    const text = textRef.current
    if (!text || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => {
      measureOverflow()
    })
    observer.observe(text)
    return () => observer.disconnect()
  }, [isActive, label, measureOverflow])

  return (
    <Tooltip open={isHovered && isOverflowing}>
      <TooltipTrigger asChild>
        <TabTitleTooltipTarget
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}>
          {children}
          <TabTitle>
            <TabTitleText ref={textRef}>{label}</TabTitleText>
          </TabTitle>
        </TabTitleTooltipTarget>
      </TooltipTrigger>
      <TooltipContent side='bottom' sideOffset={5}>
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

export function WorkspaceTabBar() {
  const {
    openTabs,
    setOpenTabs,
    activeTabId,
    openDefaultTab,
    activateWorkspaceTab,
    closeWorkspaceTab,
  } = useWorkspaceTabs()
  const [draggingTabId, setDraggingTabId] = useState<string | null>(null)
  const tabDrag = useDragReorder<DocumentTab>('workspace-tab-id')

  const handleDragStart = useCallback(
    (event: DragEvent, tabId: string) => {
      setDraggingTabId(tabId)
      tabDrag.handleDragStart(event, tabId)
    },
    [tabDrag]
  )

  const handleDrop = useCallback(
    (event: DragEvent, targetId: string) => {
      tabDrag.handleDrop(event, targetId)
      const reorderedTabs = tabDrag.reorder(openTabs)
      if (reorderedTabs) setOpenTabs(reorderedTabs)
      setDraggingTabId(null)
    },
    [openTabs, setOpenTabs, tabDrag]
  )

  const handleDragEnd = useCallback(() => {
    tabDrag.handleDragEnd()
    setDraggingTabId(null)
  }, [tabDrag])

  return (
    <Toolbar>
      <TabsWrapper>
        <Tabs value={activeTabId} onValueChange={activateWorkspaceTab}>
          <TabsList
            variant='browser'
            justify='start'
            width='full'
            size='sm'
            aria-label='Open pages'>
            {openTabs.map((tab) => {
              const label = getWorkspaceTabLabel(tab)
              const Icon = getWorkspaceTabIcon(tab)
              const isActive = tab.id === activeTabId
              const isPinned = tab.closable === false
              return (
                <WorkspaceTabShell
                  key={tab.id}
                  $dragging={draggingTabId === tab.id}
                  draggable={!isPinned}
                  onDragStart={(event) => handleDragStart(event, tab.id)}
                  onDragOver={(event) => tabDrag.handleDragOver(event, tab.id)}
                  onDragLeave={tabDrag.handleDragLeave}
                  onDrop={(event) => handleDrop(event, tab.id)}
                  onDragEnd={handleDragEnd}>
                  {tabDrag.dropTarget?.id === tab.id && (
                    <TabDropIndicator $position={tabDrag.dropTarget.position} />
                  )}
                  <TabsTrigger
                    variant='browser'
                    size='sm'
                    justify='start'
                    actionArea='inlineEnd'
                    value={tab.id}>
                    <WorkspaceTabTitleTooltip label={label} isActive={isActive}>
                      <TabIconSlot>
                        <Icon />
                      </TabIconSlot>
                    </WorkspaceTabTitleTooltip>
                  </TabsTrigger>
                  {!isPinned && (
                    <TabCloseButtonSlot>
                      <Button
                        variant='browserTabCloseAction'
                        size='icon-sm'
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                          event.stopPropagation()
                          closeWorkspaceTab(tab.id)
                        }}
                        aria-label={`Close ${label} tab`}>
                        <X />
                      </Button>
                    </TabCloseButtonSlot>
                  )}
                </WorkspaceTabShell>
              )
            })}
            <TabAddButtonSlot>
              <Button
                variant='browserTabAction'
                size='tab-icon'
                onClick={openDefaultTab}
                title='Add new tab'
                aria-label='Add new tab'>
                <Plus />
              </Button>
            </TabAddButtonSlot>
          </TabsList>
        </Tabs>
      </TabsWrapper>
    </Toolbar>
  )
}

/** Default 10rem; equal shrink only once the strip runs out of room. */
const WorkspaceTabShell = styled.div<{ $dragging: boolean }>`
  position: relative;
  flex: 0 1 10rem;
  width: 10rem;
  min-width: 2.75rem;
  max-width: 10rem;
  cursor: grab;
  opacity: ${({ $dragging }) => ($dragging ? 0.4 : 1)};
  transition: opacity 100ms ease;
`

const TabTitleTooltipTarget = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  flex: 1;
  width: 100%;
`
